import React, { forwardRef, useState, useEffect, useRef, useMemo } from "react";
import {
    Box, TextField, Popper, Paper, List, ListItemButton, ListItemText,
    Typography, ClickAwayListener, IconButton, Dialog, AppBar, Toolbar,
    Slide, useTheme, useMediaQuery,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { TransitionProps } from '@mui/material/transitions';
import iconMap from "../../../paginas/gestao/menu/iconmap";
import tokenService from "../../../services/tokenservice";
import { useEasyBrowserContext } from "../browser";
import { MenuItemType } from "../taskbar/taskbartypes.d";
import { ZLayers } from "../zlayes";

// ─── Transition para o Dialog mobile ─────────────────────────────────────────

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalize = (text: string) =>
    (text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// ─── Componente ───────────────────────────────────────────────────────────────

const FindTaskBar = forwardRef((_props: unknown, _ref: React.ForwardedRef<any>) => {
    const { abrirPagina, usuario } = useEasyBrowserContext()!;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [allScreens, setAllScreens] = useState<MenuItemType[]>([]);
    const [history, setHistory]       = useState<MenuItemType[]>([]);
    const [open, setOpen]             = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const [query, setQuery]           = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef     = useRef<HTMLInputElement | null>(null);

    const HISTORY_KEY = `find_history_${usuario?.login || 'default'}`;

    // ── Inicialização ─────────────────────────────────────────────────────

    useEffect(() => {
        const init = async () => {
            const menu = await tokenService.getMenu();
            if (menu) {
                try {
                    const parsed = JSON.parse(menu);
                    setAllScreens(parsed.menus || []);
                } catch (e) { console.error(e); }
            }
            const saved = localStorage.getItem(HISTORY_KEY);
            if (saved) setHistory(JSON.parse(saved));
        };
        init();
    }, [usuario?.login, HISTORY_KEY]);

    // ── Debounce ──────────────────────────────────────────────────────────

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 150);
        return () => clearTimeout(timer);
    }, [query]);

    // ── Busca ─────────────────────────────────────────────────────────────

    const filteredResults = useMemo(() => {
        const termo = normalize(debouncedQuery);
        if (!termo) return [];

        return allScreens
            .filter(item => {
                const desc  = normalize(item.descricao);
                const grupo = normalize(item.grupo?.join(" ") || "");
                return desc.includes(termo) || grupo.includes(termo);
            })
            .sort((a, b) => {
                const descA = normalize(a.descricao);
                const descB = normalize(b.descricao);
                const startsA = descA.startsWith(termo);
                const startsB = descB.startsWith(termo);
                if (startsA && !startsB) return -1;
                if (!startsA && startsB) return 1;
                return descA.localeCompare(descB);
            })
            .slice(0, 12);
    }, [debouncedQuery, allScreens]);

    // ── Selecionar item ───────────────────────────────────────────────────

    const selecionar = (item: MenuItemType) => {
        const newHistory = [item, ...history.filter(h => h.id !== item.id)].slice(0, 5);
        setHistory(newHistory);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
        abrirPagina(item);
        setQuery("");
        setOpen(false);
        setOpenMobile(false);
    };

    // ── Atalho Alt+Q ──────────────────────────────────────────────────────

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.altKey && e.key.toLowerCase() === 'q') {
                e.preventDefault();
                if (isMobile) {
                    setOpenMobile(true);
                } else {
                    setOpen(true);
                    // pequeno delay para garantir que o Popper já está montado
                    setTimeout(() => inputRef.current?.focus(), 50);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMobile]);

    // ── Render lista ──────────────────────────────────────────────────────

    const renderLista = (itens: MenuItemType[], isHistory = false) => (
        <List dense={!isMobile} sx={{ py: 0 }}>
            {isHistory && itens.length > 0 && (
                <Typography
                    variant="caption"
                    sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: 'text.secondary', bgcolor: 'action.hover', letterSpacing: 1 }}
                >
                    RECENTES
                </Typography>
            )}
            {itens.map((item) => (
                <ListItemButton key={item.id} onClick={() => selecionar(item)} sx={{ py: 1 }}>
                    <Box sx={{ mr: 2, display: 'flex', color: 'primary.main', opacity: 0.8 }}>
                        {isHistory
                            ? <HistoryIcon fontSize="small" />
                            : (iconMap[item.icone || "default"] || iconMap["default"])}
                    </Box>
                    <ListItemText
                        primary={item.descricao}
                        secondary={item.grupo?.join(" > ")}
                        primaryTypographyProps={{ fontSize: isMobile ? '1rem' : '0.9rem', fontWeight: 600, color: 'text.primary' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                </ListItemButton>
            ))}
        </List>
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={containerRef} sx={{ position: 'relative' }}>

                {/* ── Desktop: campo de busca ── */}
                {!isMobile && (
                    <Box sx={{
                        width: 240,
                        bgcolor: 'background.paper',
                        borderRadius: '8px',
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid',
                        borderColor: open ? 'primary.main' : 'divider',
                        boxShadow: open ? 2 : 0,
                        transition: '0.2s',
                    }}>
                        <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                        <TextField
                            inputRef={inputRef}
                            placeholder="Buscar tela... (Alt+Q)"
                            variant="standard"
                            fullWidth
                            autoComplete="off"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setOpen(true)}
                            InputProps={{ disableUnderline: true, sx: { fontSize: 13, height: 36 } }}
                        />
                    </Box>
                )}

                {/* ── Mobile: ícone de busca ── */}
                {isMobile && (
                    <IconButton onClick={() => setOpenMobile(true)} color="inherit">
                        <SearchIcon />
                    </IconButton>
                )}

                {/* ── Desktop: dropdown de resultados ──────────────────────────────────
                  *
                  * O Popper do MUI já faz createPortal para document.body por padrão,
                  * escapando do translateX das páginas.
                  *
                  * O z-index precisa ser acima da TaskBar (ZLayers.global.taskBar = 1200),
                  * por isso usamos ZLayers.global.taskBarDropdown = 1300.
                  *
                  * NÃO usar ZIndexContext aqui — esse é um elemento global da TaskBar,
                  * não pertence ao stacking context de nenhuma página.
                  */}
                <Popper
                    open={open && !isMobile}
                    anchorEl={containerRef.current}
                    placement="bottom-start"
                    // disablePortal padrão é false — já vai para document.body ✓
                    style={{
                        zIndex: ZLayers.global.taskBarDropdown,
                        width: 350,
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            mt: 1,
                            maxHeight: 450,
                            overflow: 'auto',
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {query.trim() === ""
                            ? renderLista(history, true)
                            : renderLista(filteredResults)}
                        {query && filteredResults.length === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Nenhuma tela encontrada
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Popper>

                {/* ── Mobile: dialog fullscreen ─────────────────────────────────────
                  *
                  * Dialog também vai para document.body.
                  * style={{ zIndex }} garante que fique acima da TaskBar.
                  */}
                <Dialog
                    fullScreen
                    open={openMobile}
                    onClose={() => setOpenMobile(false)}
                    TransitionComponent={Transition}
                    style={{ zIndex: ZLayers.global.taskBarDropdown }}
                >
                    <AppBar sx={{ position: 'relative', bgcolor: 'background.paper', color: 'text.primary', elevation: 1 }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={() => setOpenMobile(false)}>
                                <CloseIcon />
                            </IconButton>
                            <TextField
                                autoFocus
                                fullWidth
                                placeholder="Nome da tela..."
                                variant="standard"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{ disableUnderline: true, sx: { ml: 2, fontSize: '1.1rem' } }}
                            />
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ bgcolor: 'background.default', flex: 1, overflowY: 'auto' }}>
                        {query.trim() === ""
                            ? renderLista(history, true)
                            : renderLista(filteredResults)}
                    </Box>
                </Dialog>

            </Box>
        </ClickAwayListener>
    );
});

export default FindTaskBar;